// Which term ("AI" or "UI") the pages use: the signed-in company's setting in the app, the
// company's setting from the public data on the pages for its staff, and "AI" everywhere else
// (landing, login, privacy, the Agentcija admin page). When the term changes, the pages remount,
// so every text is read again through the term layer (i18n/termLayer.ts).

import { Fragment, useEffect, useSyncExternalStore, type ReactNode } from "react";
import { useLocation } from "react-router";
import { asTerm, type AiTerm } from "../i18n/term";
import { setCurrentTerm } from "../i18n/termLayer";
import { useSession } from "./session";

/** Pages for the company's staff without login: survey, report new use, training, builder check. */
const PUBLIC_PAGE = /^\/(a|p|u|s)\/[^/]+/;

// ---------------------------------------------------------------- terms of public pages

const publicTerms = new Map<string, AiTerm>();
const publicListeners = new Set<() => void>();
let publicVersion = 0;

function publicKey(pathname: string): string | null {
  return PUBLIC_PAGE.exec(pathname)?.[0] ?? null;
}

/** A staff page learned its company's term from its public data. */
export function usePublicTerm(term: string | null | undefined): void {
  const { pathname } = useLocation();
  useEffect(() => {
    const key = publicKey(pathname);
    if (!key || term === undefined || term === null) return;
    const next = asTerm(term);
    if (publicTerms.get(key) === next) return;
    publicTerms.set(key, next);
    publicVersion++;
    publicListeners.forEach((l) => l());
  }, [term, pathname]);
}

function subscribePublic(l: () => void): () => void {
  publicListeners.add(l);
  return () => publicListeners.delete(l);
}

const publicSnapshot = () => publicVersion;

// ---------------------------------------------------------------- data of public pages

const publicLoads = new Map<string, Promise<unknown>>();

/**
 * Public page data loaded once per page: the remount after the term is known reuses it (no second
 * request, and the training attempt stays the same). `fresh` loads again (for example "Osveži").
 */
export function publicLoad<T>(key: string, load: () => Promise<T>, fresh = false): Promise<T> {
  let p = fresh ? undefined : (publicLoads.get(key) as Promise<T> | undefined);
  if (!p) {
    p = load();
    publicLoads.set(key, p);
    p.catch(() => publicLoads.delete(key));
  }
  return p;
}

// ---------------------------------------------------------------- the root

/** The term of a page: "org" pages use the company's setting, public pages their own. */
export function termFor(pathname: string, orgTerm: unknown): AiTerm {
  if (pathname === "/app" || pathname.startsWith("/app/") || pathname === "/preveri") return asTerm(orgTerm);
  const key = publicKey(pathname);
  if (key) return publicTerms.get(key) ?? "AI";
  return "AI";
}

/** Sets the term for the routes below and remounts them when it changes. */
export function TermRoot({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const { pathname } = useLocation();
  useSyncExternalStore(subscribePublic, publicSnapshot, publicSnapshot);
  const term = termFor(pathname, me?.org?.ai_term);
  // set during render: the pages below read their texts in this render
  setCurrentTerm(term);
  return <Fragment key={term}>{children}</Fragment>;
}
